<template>
  <div>
    <slot :data="measurementResults" :isFetchingData="isFetchingData" />
  </div>
</template>

<script>
  import { consoleHelpers } from 'utils/logHelpers';
  import { UNKNOWN_VALUE } from '@/utils/chart/chart-constants';
  import { apiMeasurements } from '@/api/graphql/cloud/measurements';
  import { isInjectionInProgress } from '@/utils/injectionHelpers';
  import { mergeDataObjects } from '@/utils/measurementHelpers';

  const EVENT_CREATE = 'create';
  const EVENT_DESTROY = 'destroy';

  export default {
    name: 'PrDataAdapter',

    props: {
      measurements: {
        type: Array,
        required: true,
      },
      sampleToken: {
        type: String,
        default: null,
      },
      hasLiveUpdate: {
        type: Boolean,
        default: true,
      },
      doSkipDataFetching: {
        type: Boolean,
      },
      sampleId: {
        type: Number,
        required: true,
      },
      injection: {
        type: Object,
      },
    },

    data() {
      return {
        activeSockets: [],
        measurementResults: [],
        measurementSubscriptions: [],
        isFetchingData: true,
        isInjectionInProgress: false,
        socketsInitializationId: 0,
        measurementsInitializationId: 0,
      };
    },

    watch: {
      measurements: {
        async handler(measurements) {
          this.isFetchingData = true;

          // Invalidate any in-flight initMeasurements run to prevent leaking subscriptions
          this.measurementsInitializationId += 1;
          const runId = this.measurementsInitializationId;

          // Ensure previous measurement subscriptions are cleaned up before creating new ones
          this.destroyMeasurementSubscriptions();

          if (this.doSkipDataFetching) {
            this.measurementResults = this.measurements;
            this.isFetchingData = false;
            return;
          }

          const results = await this.initMeasurements(measurements, runId);

          // If a newer run started, do not override state or start sockets
          if (runId !== this.measurementsInitializationId) {
            return;
          }

          this.measurementResults = results.filter(Boolean);

          this.isFetchingData = false;

          this.initSockets();
        },
        immediate: true,
      },
      'injection.state': {
        handler() {
          this.isInjectionInProgress = isInjectionInProgress(this.injection);

          this.isFetchingData = true;
        },
        immediate: true,
      },
      isInjectionInProgress(isInProgress) {
        this.activeSockets.forEach((socket) =>
          isInProgress ? socket.connection.resume() : socket.connection.pause(),
        );
      },
    },

    created() {
      this.$emit(EVENT_CREATE);
    },

    beforeDestroy() {
      console.warn('beforeDestroy');

      this.socketsInitializationId += 1;
      this.measurementsInitializationId += 1;

      this.destroyMeasurementSubscriptions();
      this.destroySockets();
      this.$emit(EVENT_DESTROY);
    },

    methods: {
      async initSockets() {
        this.socketsInitializationId += 1;
        const runId = this.socketsInitializationId;
        const socketIdsShouldBeInit = this.measurements.map(({ id }) => id);
        consoleHelpers.warn('Socket ids', socketIdsShouldBeInit);
        const socketsWithoutClosed = this.activeSockets.filter((socket) => {
          if (socketIdsShouldBeInit.includes(socket.measurementId)) return true;
          socket.connection.unsubscribe();
          return false;
        });
        consoleHelpers.warn('Sockets without closed', socketsWithoutClosed);

        const nextSockets = await (!this.hasLiveUpdate
          ? []
          : Promise.all(
              this.measurements.map((measurement) => {
                const alreadyConnectedSocket = socketsWithoutClosed.find(
                  (socket) => socket.measurementId === measurement.id,
                );
                return (
                  alreadyConnectedSocket ?? this.createSocket(measurement.id, measurement.ulid)
                );
              }),
            ));

        if (runId !== this.socketsInitializationId) {
          // A newer initSockets run started; dispose sockets created by this run
          nextSockets.forEach((socket) => socket.connection?.unsubscribe?.());
          return;
        }
        this.activeSockets = nextSockets;
      },

      async createSocket(measurementId, measurementUlid) {
        const onNewData = (from, values) => {
          this.measurementResults = this.measurementResults.map((_measurement) => {
            if (_measurement?.id === measurementId) {
              const currentData = _measurement?.data;
              const currentLength = currentData?.length ?? 0;

              if (currentLength === from) {
                const data = [...(currentData ?? []), ...values];
                return {
                  ..._measurement,
                  data,
                  data_length: data.length,
                };
              } else if (currentLength < from) {
                const difference = from - currentLength;
                const unknownValues = Array(difference).fill(UNKNOWN_VALUE);
                const data = [...(currentData ?? []), ...unknownValues, ...values];

                return {
                  ..._measurement,
                  data,
                  data_length: data.length,
                };
              }
              const data = [...(currentData ?? [])];
              data.splice(from, values.length, ...values);

              return {
                ..._measurement,
                data,
                data_length: data.length,
              };
            }
            return _measurement;
          });
        };

        const subscription = apiMeasurements.subscribeForMeasurementsData(
          measurementUlid,
          (measurementsData) => {
            if (measurementsData?.length !== 0) {
              onNewData(
                measurementsData[0].from,
                measurementsData.length === 1
                  ? measurementsData[0].values
                  : mergeDataObjects(measurementsData),
              );
            }
          },
          this.sampleToken,
          this.sampleId,
          { isPaused: !this.isInjectionInProgress },
        );

        await subscription.isConnected;

        return {
          connection: subscription,
          measurementId,
        };
      },
      destroySockets() {
        if (!this.hasLiveUpdate) {
          return;
        }

        this.activeSockets.forEach((socket) => socket.connection.unsubscribe());
        this.activeSockets = [];
      },

      destroyMeasurementSubscriptions() {
        if (Array.isArray(this.measurementSubscriptions) && this.measurementSubscriptions.length) {
          this.measurementSubscriptions.forEach((sub) => sub?.unsubscribe?.());
          this.measurementSubscriptions = [];
        }
      },

      initMeasurements(measurements, runId) {
        return Promise.all(
          measurements.map(async (measurementShort) => {
            // Start subscription first to catch any incoming updates
            const subscription = apiMeasurements.subscribeForMeasurement(
              measurementShort.id,
              async (m) => {
                if (runId !== this.measurementsInitializationId) {
                  subscription?.unsubscribe?.();
                  return;
                }

                const currentMeasurement = this.measurementResults.find(
                  (measurement) => measurement.id === m.id,
                );

                const _data = await m.getData(false);

                const data = currentMeasurement?.data
                  ? _data?.length >= currentMeasurement.data?.length
                    ? _data
                    : currentMeasurement.data
                  : _data;

                // Update the specific measurement in-place
                this.measurementResults = this.measurementResults.map((mm) =>
                  mm.id === m.id
                    ? {
                        ...currentMeasurement,
                        ...m,
                        baseline: [],
                        data,
                        data_length: data.length,
                      }
                    : mm,
                );
              },
              this.sampleToken,
              this.sampleId,
            );

            // If a newer run started, clean up and skip
            if (runId !== this.measurementsInitializationId) {
              subscription?.unsubscribe?.();
              return null;
            }

            // Track subscription to properly unsubscribe later (only for active run)
            this.measurementSubscriptions.push(subscription);

            // Fetch initial snapshot to render immediately
            const m = await apiMeasurements.getMeasurement(
              measurementShort.id,
              this.sampleId,
              this.sampleToken,
            );

            const _data = await m.getData(false);

            const currentMeasurement = this.measurementResults.find(
              (measurement) => measurement.id === m.id,
            );

            const data = currentMeasurement?.data
              ? _data?.length >= currentMeasurement.data?.length
                ? _data
                : currentMeasurement.data
              : _data;

            if (runId !== this.measurementsInitializationId) {
              subscription?.unsubscribe?.();
              return null;
            }

            return {
              ...currentMeasurement,
              ...m,
              baseline: [],
              data,
              data_length: data.length,
            };
          }),
        );
      },
    },
  };
</script>
